import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { i18n } from "./lang/i18n";

const t = (key) => i18n.global.t(key);

const BRAND = "0B4F9C";
const HEADER_TEXT = "FFFFFFFF";
const ZEBRA = "F8FAFC";
const BORDER = "E5E7EB";

function thinBorder(color = BORDER) {
  const side = { style: "thin", color: { argb: color } };
  return { top: side, left: side, bottom: side, right: side };
}

function autoColumnWidths(sheet, fromRow, toRow, fromCol, toCol) {
  for (let col = fromCol; col <= toCol; col += 1) {
    let max = 10;
    for (let row = fromRow; row <= toRow; row += 1) {
      const cell = sheet.getCell(row, col);
      const value = cell.value == null ? "" : String(cell.value);
      max = Math.max(max, Math.min(value.length + 2, 42));
    }
    sheet.getColumn(col).width = max;
  }
}

/**
 * @param {Object} options
 * @param {string} [options.fileName]
 * @param {string} [options.sheetName]
 * @param {Array<Record<string, string|number|null|undefined>>} options.rows
 */
export async function exportStyledTableWorkbook({
  fileName = "export.xlsx",
  sheetName = "Nəticə",
  rows = [],
}) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Progress IMS";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: 1, showGridLines: true }],
  });

  const headers = rows.length ? Object.keys(rows[0]) : [];
  const headerRowIndex = 1;
  const headerRow = sheet.getRow(headerRowIndex);
  headerRow.height = 22;

  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { name: "Calibri", size: 11, bold: true, color: { argb: HEADER_TEXT } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND } };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = thinBorder(BRAND);
  });

  rows.forEach((row, rowIndex) => {
    const excelRow = sheet.getRow(headerRowIndex + 1 + rowIndex);
    excelRow.height = 18;
    const isEven = rowIndex % 2 === 1;
    headers.forEach((header, colIndex) => {
      const cell = excelRow.getCell(colIndex + 1);
      const raw = row[header];
      cell.value = raw == null || raw === "" ? "—" : raw;
      cell.font = { name: "Calibri", size: 10, color: { argb: "1F2937" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: isEven ? ZEBRA : "FFFFFF" },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: colIndex === 0 ? "center" : "left",
        wrapText: true,
      };
      cell.border = thinBorder();
    });
  });

  if (headers.length && rows.length) {
    sheet.autoFilter = {
      from: { row: headerRowIndex, column: 1 },
      to: { row: headerRowIndex + rows.length, column: headers.length },
    };
  }

  autoColumnWidths(
    sheet,
    headerRowIndex,
    headerRowIndex + rows.length,
    1,
    headers.length
  );

  sheet.properties.defaultRowHeight = 18;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`);
}

export function mapInsuredRowsForExport(items = []) {
  return items.map((item) => ({
    PIN: item.pin || "",
    [t("commonFullName")]: item.insuredName || "",
    VIP: item.vip ? "VIP" : "—",
    [t("commonCard")]: item.cardFullNumber || "",
    [t("commonProgram")]: item.groupName || "",
    [t("commonStatus")]: item.employeeStatusName || "",
    [t("commonSex")]: item.sexStr || "",
    [t("commonBirthDate")]: item.dob ? item.dob.slice(0, -9) : "",
    [t("commonPhoneShort")]: item.phoneMobile || "",
    [t("commonAddress")]: item.address || "",
    [t("commonStart")]: item.startDateStr || "",
    [t("commonEnd")]: item.endDateStr || "",
    [t("commonTerminate")]: item.cancelDateStr || "",
  }));
}
